import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FlaskConical, Filter, Mail, Phone, Globe, ChevronDown, Check, MapPin, Wifi, Sparkles, Loader2, RefreshCw, AlertTriangle, Trash2 } from "lucide-react";
import api from "@/lib/api";
import ResidencyOfferPanel from "@/components/admin/ResidencyOfferPanel";
import { Button } from "@/components/ui/button";

const STATUSES = ["pending", "reviewing", "accepted", "waitlisted", "rejected", "enrolled"];

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  reviewing: "bg-blue-50 text-blue-700 border-blue-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  enrolled: "bg-blue-50 text-blue-800 border-blue-200",
  waitlisted: "bg-slate-50 text-slate-700 border-slate-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

const PROGRAM_META = {
  "residency-bench-side": { label: "Bench-side", Icon: MapPin, tone: "text-amber-700 bg-amber-50 border-amber-200" },
  "residency-remote-lab": { label: "Remote Lab", Icon: Wifi, tone: "text-blue-700 bg-blue-50 border-blue-200" },
  "residency-solo-bench": { label: "Solo Bench", Icon: Sparkles, tone: "text-slate-800 bg-slate-100 border-slate-300" },
};

const FIT_STYLES = {
  strong: "bg-emerald-100 text-emerald-800 border-emerald-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-rose-100 text-rose-800 border-rose-200",
};

const STAGE_LABELS = {
  brand_new: "Brand new",
  dabbling: "Dabbling",
  losing_money: "Losing money",
  breakeven: "Roughly breakeven",
  consistent_small: "Consistent but small",
  profitable_scaling: "Profitable, scaling",
};

const BUDGET_LABELS = {
  under_1k: "< $1k",
  "1_5k": "$1k–5k",
  "5_10k": "$5k–10k",
  "10k_plus": "$10k+",
  flexible: "Flexible",
};

export default function AdminApplications() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [summarizing, setSummarizing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/applications", { params });
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load applications");
    }
  }, [filter]);

  useEffect(() => { refresh(); }, [refresh]);

  const generateSummary = async (id) => {
    setSummarizing(id);
    try {
      const { data } = await api.post(`/admin/applications/${id}/summary`);
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ai_summary: data } : r)));
      toast.success("Summary generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate summary");
    } finally {
      setSummarizing(null);
    }
  };

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      toast.success(`Marked ${status}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setUpdating(null);
    }
  };

  const deleteApplication = async (id, name) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Permanently delete application from ${name || "this applicant"}? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      await api.delete(`/admin/applications/${id}`);
      toast.success("Application deleted");
      setRows((rs) => rs.filter((r) => r.id !== id));
      if (expanded === id) setExpanded(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div data-testid="admin-applications" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-amber-600 font-semibold mb-2">
            <FlaskConical size={12} /> The Residency
          </div>
          <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight">
            Applications
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Bench-side · Remote Lab · Application only, no direct checkout
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-1 py-1 overflow-x-auto">
          <Filter size={14} className="text-slate-500 ml-2 shrink-0" />
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`admin-applications-filter-${s}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-widest transition-colors ${
                filter === s
                  ? "bg-blue-900 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500 text-sm">
          No applications match that filter yet.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
          {rows.map((r) => {
            const open = expanded === r.id;
            return (
              <div key={r.id} data-testid={`admin-application-row-${r.id}`} className="p-5 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-4 items-start">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-display font-bold text-slate-900">
                        {r.full_name}
                      </span>
                      {(() => {
                        const pm = PROGRAM_META[r.program_id];
                        if (!pm) return null;
                        const PIcon = pm.Icon;
                        return (
                          <span
                            data-testid={`admin-application-program-${r.program_id}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] uppercase tracking-widest font-semibold ${pm.tone}`}
                          >
                            <PIcon size={10} /> {pm.label}
                          </span>
                        );
                      })()}
                      <span className={`inline-flex px-2 py-0.5 rounded border text-[10px] uppercase tracking-widest font-semibold ${STATUS_STYLES[r.status] || STATUS_STYLES.pending}`}>
                        {r.status}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Mail size={11} />{r.email}</span>
                      <span className="inline-flex items-center gap-1"><Phone size={11} />{r.phone}</span>
                      <span className="inline-flex items-center gap-1"><Globe size={11} />{r.country}</span>
                      <span>{r.experience_years}y exp</span>
                      {r.account_size_usd ? <span>${Number(r.account_size_usd).toLocaleString()} capital</span> : null}
                      <span>{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center md:max-w-[420px] md:justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={summarizing === r.id}
                      onClick={() => generateSummary(r.id)}
                      data-testid={`admin-application-${r.id}-summarize`}
                      className="h-8 px-3 text-xs border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                    >
                      {summarizing === r.id ? (
                        <><Loader2 size={12} className="mr-1.5 animate-spin" /> Reading…</>
                      ) : r.ai_summary ? (
                        <><RefreshCw size={12} className="mr-1.5" /> Regenerate</>
                      ) : (
                        <><Sparkles size={12} className="mr-1.5" /> AI summary</>
                      )}
                    </Button>
                    {STATUSES.filter((s) => s !== r.status).map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant="outline"
                        disabled={updating === r.id}
                        onClick={() => updateStatus(r.id, s)}
                        data-testid={`admin-application-${r.id}-set-${s}`}
                        className="h-8 px-3 text-xs capitalize border-slate-200 hover:border-blue-800 hover:text-blue-900"
                      >
                        {updating === r.id ? "…" : s}
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={deleting === r.id}
                      onClick={() => deleteApplication(r.id, r.full_name)}
                      data-testid={`admin-application-${r.id}-delete`}
                      className="h-8 px-3 text-xs border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800 hover:border-rose-400"
                    >
                      {deleting === r.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <><Trash2 size={12} className="mr-1.5" /> Delete</>
                      )}
                    </Button>
                  </div>
                  <button
                    onClick={() => setExpanded(open ? null : r.id)}
                    data-testid={`admin-application-${r.id}-toggle`}
                    className="text-xs font-semibold text-blue-900 hover:text-blue-700 inline-flex items-center gap-1 shrink-0 self-start"
                  >
                    {open ? "Hide" : "Details"} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                </div>

                {["accepted", "enrolled"].includes(r.status) && <ResidencyOfferPanel appId={r.id} />}

                {/* Full-width AI summary strip on collapsed rows so scanning is easy */}
                {r.ai_summary && (
                  <div
                    data-testid={`admin-application-${r.id}-summary`}
                    className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3"
                  >
                    <Sparkles size={14} className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 leading-snug">{r.ai_summary.summary}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {r.ai_summary.fit_signal && (
                          <span
                            data-testid={`admin-application-${r.id}-fit`}
                            className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-widest font-semibold ${FIT_STYLES[r.ai_summary.fit_signal] || FIT_STYLES.medium}`}
                          >
                            fit · {r.ai_summary.fit_signal}
                          </span>
                        )}
                        {r.ai_summary.suggested_program && r.ai_summary.suggested_program !== r.program_id && (() => {
                          const sm = PROGRAM_META[r.ai_summary.suggested_program];
                          if (!sm) return null;
                          const SIcon = sm.Icon;
                          return (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-slate-600">
                              <span>Suggested:</span>
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${sm.tone}`}>
                                <SIcon size={9} /> {sm.label}
                              </span>
                            </span>
                          );
                        })()}
                        {(r.ai_summary.risk_flags || []).map((flag) => (
                          <span
                            key={flag}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-rose-200 bg-rose-50 text-rose-700 text-[10px] uppercase tracking-widest font-semibold"
                          >
                            <AlertTriangle size={9} /> {flag.replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {open && (
                  <div className="space-y-4">
                    {/* Full AI summary card if present */}
                    {r.ai_summary && (
                      <div
                        data-testid={`admin-application-${r.id}-summary-full`}
                        className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-xl p-5"
                      >
                        <div className="flex items-start gap-3">
                          <Sparkles size={16} className="text-amber-600 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-semibold">
                              Desk read · {new Date(r.ai_summary.generated_at).toLocaleDateString()}
                            </div>
                            <p className="mt-1 text-sm text-slate-900 leading-relaxed">{r.ai_summary.summary}</p>
                            {r.ai_summary.suggested_program_reason && (
                              <p className="mt-2 text-xs text-slate-600">
                                <span className="font-semibold text-slate-800">
                                  Suggested {PROGRAM_META[r.ai_summary.suggested_program]?.label || r.ai_summary.suggested_program}:
                                </span>{" "}
                                {r.ai_summary.suggested_program_reason}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-slate-50 border border-slate-100 rounded-xl p-4">
                      <Field label="Current stage" value={STAGE_LABELS[r.current_stage] || r.current_stage || "—"} />
                      <Field label="Education budget" value={BUDGET_LABELS[r.education_budget] || r.education_budget || "—"} />
                      <Field label="Commitment (1-10)" value={r.commitment_level ?? "—"} />
                      <Field label="Markets traded" value={r.markets_traded} />
                      <Field label="Referral source" value={r.referral_source || "—"} />
                      <Field label="Linked user id" value={r.user_id || "—"} />
                      <div className="md:col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Biggest struggle</div>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{r.biggest_struggle || "—"}</p>
                      </div>
                      <div className="md:col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Why now</div>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{r.why_now || "—"}</p>
                      </div>
                      <div className="md:col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Goals</div>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{r.goals}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</div>
      <div className="text-slate-800 flex items-center gap-1">
        <Check size={12} className="text-emerald-600 shrink-0" />
        <span className="truncate">{value}</span>
      </div>
    </div>
  );
}
